/**
 * A single KPI stat, pre-formatted by the caller and ready to hand straight to
 * `components/carbon/StatCard`'s `label`/`value`/`caption`/`icon` props. Used to be
 * `{ name, value, icon, color, description?, currency? }` — `color` drove the pre-Carbon
 * `KpiCard`'s Tailwind `bg-*` accent (one color per currency, via `CURRENCY_COLORS`/
 * `CURRENCY_SAVINGS_COLORS` below) and `currency` only ever fed that same color lookup, never
 * rendered. The Dashboards-flow Carbon migration (Task 4) replaced `KpiCard` with the shared
 * `StatCard` primitive, which is a neutral Carbon `Tile` with a single restrained icon accent
 * token baked in (`stat-card__icon`'s `var(--cds-icon-secondary)`) rather than a per-card
 * rainbow — so both fields, and the color tables that fed them, are dead and removed here.
 */
export interface KpiCard {
  name: string;
  value: string | number;
  icon: React.ElementType;
  description?: string;
}

/**
 * Local YYYY-MM-DD (no timezone shift, unlike `toISOString`) — same convention as
 * `DeliveryConfirmation.tsx`'s own `toIsoDate`. Used by `AnalyticsFilterBar` to turn
 * a Carbon `DatePicker` range's `selectedDates` (`Date[]`) back
 * into the plain ISO date strings their `onDateRangeChange` contract has always emitted (the
 * pre-Carbon controls read these straight off native `<input type="date">` elements).
 */
export const toIsoDate = (date?: Date): string =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`
    : "";

export const formatCurrency = (amount: number, currency: string): string => {
  if (amount == null || currency == null) {
    return "0";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    currencyDisplay: "code",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatPercentage = (value: number | null): string => {
  if (value == null) {
    return "0%";
  }
  return `${value.toFixed(1)}%`;
};

export const getMainCurrency = (amountByCurrency: {
  [currency: string]: number;
}): string => {
  if (!amountByCurrency || Object.keys(amountByCurrency).length === 0) {
    return "USD";
  }

  const currencies = Object.entries(amountByCurrency);
  const [mainCurrency] = currencies.sort((a, b) => b[1] - a[1]);
  return mainCurrency[0];
};

export const createCurrencyCards = (
  baseName: string,
  amountByCurrency: { [currency: string]: number } | undefined,
  icon: React.ElementType,
  description?: string
): KpiCard[] => {
  if (!amountByCurrency || Object.keys(amountByCurrency).length === 0) {
    return [
      {
        name: baseName,
        value: "$0",
        icon,
        description,
      },
    ];
  }

  const currencies = Object.entries(amountByCurrency);

  return currencies.map(([currency, amount]) => ({
    name: `${baseName} (${currency})`,
    value: formatCurrency(amount, currency),
    icon,
    description,
  }));
};

export const createPercentageCards = (
  baseName: string,
  percentageByCurrency: { [currency: string]: number } | undefined,
  icon: React.ElementType,
  description?: string
): KpiCard[] => {
  if (!percentageByCurrency || Object.keys(percentageByCurrency).length === 0) {
    return [
      {
        name: baseName,
        value: "0%",
        icon,
        description,
      },
    ];
  }

  const currencies = Object.entries(percentageByCurrency);

  return currencies.map(([currency, percentage]) => ({
    name: `${baseName} (${currency})`,
    value: `${percentage.toFixed(1)}%`,
    icon,
    description,
  }));
};
